import React, { useCallback, useState, useEffect, useRef } from 'react';
import { MODAL_AUTOMATA } from './ModalWrapper';
import { EditTimeModalWrapper } from './MainCalendar.style';
import { Select, TextArea } from 'views/containers/form';
import { Field, Formik } from 'formik';
import { APPOINTMENT_BY_MINUTE_VALIDATOR } from 'utils/validators/appointmentByMinute';
import moment from 'moment';
import { Button } from 'lib/ui';
import { useMutation } from '@apollo/react-hooks';
import { EDIT_EVENT_TIME } from 'api/graphql/v2/mutations/Event';
import { DEFAULT_EDIT_TIME_NOTE } from 'utils/constants/default';

// both are out of the functional component to avoid unnecessary rerenders

// init the options fields
// offers the full day (12:00 am to 11:59 pm) for both start and end time;
// start/end ordering is enforced by APPOINTMENT_BY_MINUTE_VALIDATOR instead
// of restricting which options are selectable.
export const setOptions = (
  startTime,
  endTime,
  originalStartDate,
  originalEndDate,
  validOptions,
  setStartTimeOptions,
  setEndTimeOptions
) => {
  // comparing to the originals always
  // the only integrity condition needed is originalStart and end
  const refStartTime = originalStartDate || startTime;
  // offsets (in minutes) are relative to refStartTime, so an offset of 0
  // reproduces the original start time
  const dayStart = moment(refStartTime).clone().startOf('day');
  const baseOffset = dayStart.diff(moment(refStartTime), 'minutes');
  const minutesInDay = 24 * 60;

  const timeMoment = dayStart.clone();
  const options = Array.from({ length: minutesInDay }, (_, minute) => {
    const option = {
      value: baseOffset + minute,
      label: timeMoment.format('hh:mm a')
    };
    timeMoment.add(1, 'minutes');
    return option;
  });

  // set ref for further use not re use computations
  validOptions.current = options;
  setStartTimeOptions(options);
  setEndTimeOptions(options);
};

export const getStartOffset = (originalStartDate, startTime) => {
  return originalStartDate
    ? moment(startTime).diff(originalStartDate, 'minutes')
    : 0;
};

export const getEndOffset = (originalStartDate, startTime, endTime) => {
  return (
    moment(endTime).diff(moment(startTime), 'minutes') +
    (originalStartDate
      ? moment(startTime).diff(originalStartDate, 'minutes')
      : 0)
  );
};

export const ModalEditTime = ({ setStep, event, closeModal }) => {
  const handleCancel = useCallback(() => {
    setStep(MODAL_AUTOMATA.MODAL_CONTENT);
  }, [setStep]);

  const [startTimeOptions, setStartTimeOptions] = useState([]);
  const [endTimeOptions, setEndTimeOptions] = useState([]);
  const validOptions = useRef([]);

  const [editEventTime] = useMutation(EDIT_EVENT_TIME);

  const onSubmit = useCallback(
    (values, actions) => {
      actions.setSubmitting(true);
      // taking the absolute truth of the start
      const eventStart = moment(event.originalStartDate || event.startDate);
      editEventTime({
        variables: {
          data: {
            event: {
              startDate: eventStart
                .clone()
                .add(values.startTimeOffset, 'minutes')
                .toISOString(),
              endDate: eventStart
                .clone()
                .add(values.endTimeOffset, 'minutes')
                .toISOString(),
              id: values.id
            },
            notes: values.notes
          }
        }
      })
        .then(() => {
          actions.setSubmitting(false);
          closeModal();
        })
        .catch(err => {
          console.log(err);
          closeModal();
        });
    },
    [event.originalStartDate, event.startDate, editEventTime, closeModal]
  );

  useEffect(() => {
    setOptions(
      event.startDate,
      event.endDate,
      event.originalStartDate,
      event.originalEndDate,
      validOptions,
      setStartTimeOptions,
      setEndTimeOptions
    );
  }, [event]);

  return (
    <EditTimeModalWrapper>
      <Formik
        initialValues={{
          startTimeOffset: getStartOffset(
            event.originalStartDate,
            event.startDate
          ),
          endTimeOffset: getEndOffset(
            event.originalStartDate,
            event.startDate,
            event.endDate
          ),
          notes: DEFAULT_EDIT_TIME_NOTE,
          id: event.id
        }}
        enableReinitialize
        isInitialValid={false}
        validationSchema={APPOINTMENT_BY_MINUTE_VALIDATOR}
        onSubmit={(values, actions) => onSubmit(values, actions)}
        render={({
          values,
          isValid,
          handleSubmit,
          isSubmitting,
          dirty,
          setFieldValue
        }) => (
          <>
            <h1 className="title">Edit Appointment Time</h1>
            <div className="form">
              <div className="item">
                <label className="label">Start Time</label>
                <Field
                  name="startTimeOffset"
                  value={values.startTimeOffset}
                  render={fieldProps => (
                    <Select
                      name="startTimeOffset"
                      placeholder="Start Time"
                      options={startTimeOptions}
                      value={values.startTimeOffset}
                      {...fieldProps}
                    />
                  )}
                />
              </div>

              <div className="item">
                <label className="label">End Time</label>
                <Field
                  name="endTimeOffset"
                  value={values.endTimeOffset}
                  key="endTimeOffset"
                  render={fieldProps => (
                    <Select
                      name="endTimeOffset"
                      placeholder="End Time"
                      value={values.endTimeOffset}
                      options={endTimeOptions}
                      {...fieldProps}
                    />
                  )}
                />
              </div>
            </div>

            <div className="notes-wrapper">
              <label className="label">Notes</label>
              <Field
                name="notes"
                value={values.notes}
                render={fieldProps => (
                  <TextArea
                    name="notes"
                    placeholder="N/A"
                    value={values.notes}
                    onChange={e => setFieldValue('notes', e.target.value)}
                  />
                )}
              />
            </div>

            <div className="buttons">
              <Button
                className="button secondary"
                onClick={handleCancel}
                loading={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                className={`button ${
                  !isValid || !dirty ? 'disabled' : 'primary'
                }`}
                disabled={!isValid || !dirty}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                <span className="underlineSpan">OK</span>
              </Button>
            </div>
          </>
        )}
      />
    </EditTimeModalWrapper>
  );
};
